import { useMutation, useQuery } from "@tanstack/react-query";

import {
  INDIAN_ISD_CODE,
  LEARN_VERTICAL_ID,
  SCA_VERTICAL_ID,
} from "constants/constants";
import { DAY, QUERY_RETRY_ATTEMPTS } from "constants/queryCacheTimes";
import Cookies from "js-cookie";
import { toISTDateTime } from "utils/formatDate";

const transformKHUserToUserFormat = (khUser) => {
  return [
    {
      firstName: khUser.first_name,
      lastName: khUser.last_name,
      phone: khUser.phone,
      uamId: khUser.uam_id,
      verticalId: SCA_VERTICAL_ID,
    },
  ];
};

const fetchKHUser = async (phoneNumber) => {
  const url = process.env.REACT_APP_GET_VERIFY_USER;

  const authToken = Cookies.get("jweToken");

  const payload = { isd_code: INDIAN_ISD_CODE, phone: phoneNumber };

  const headers = {
    accept: "application/json",
    "product-id": "900",
    "tenant-id": "7",
    platform: "web",
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    const errorMessage = errorBody?.detail?.message || "Something went wrong.";
    throw new Error(errorMessage);
  }

  const khUserData = await response.json();

  if (!khUserData?.users || khUserData.users.length === 0) {
    throw new Error("No user found for the provided phone number or UAM ID.");
  }

  const transformedKHUsers = transformKHUserToUserFormat(khUserData.users[0]);
  return transformedKHUsers;
};

const transformLearnUsersWithValidation = (globalUsers) => {
  if (!globalUsers || !Array.isArray(globalUsers.accounts)) return [];

  return globalUsers.accounts.flatMap((account) => {
    if (!Array.isArray(account.profiles)) return [];

    const matchingProfiles = account.profiles.filter(
      (profile) => Number(profile.vertical_id) === Number(LEARN_VERTICAL_ID)
    );

    if (matchingProfiles.length > 1) {
      throw new Error(
        "Seems like there is an issue with this customer's phone number/UAM ID. Please escalate this customer to the support team for resolution."
      );
    }

    return matchingProfiles.map((profile) => ({
      firstName: account.first_name,
      lastName: account.last_name,
      phone: account.phone,
      verticalId: profile.vertical_id,
      uamId: profile.uam_id,
    }));
  });
};

const fetchLearnUser = async (phoneNumber) => {
  const authToken = Cookies.get("jweToken");
  const url = process.env.REACT_APP_ONE_APP_SEARCH_USER_URL;

  const payload = { isd_code: INDIAN_ISD_CODE, phone: phoneNumber };

  const headers = {
    accept: "application/json",
    "product-id": "100",
    "tenant-id": "99999",
    platform: "web",
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const globalUsers = await response.json();

  if (response.status !== 200) {
    throw new Error("An error occurred while requesting Customer Information.");
  }

  const filtered_user_data = transformLearnUsersWithValidation(globalUsers);

  if (filtered_user_data.length === 0) {
    throw new Error("No user found for the provided phone number");
  }

  return filtered_user_data;
};

const fetchCustomerInfo = async (payload) => {
  if (Number(payload.selectedBusinessVertical) === SCA_VERTICAL_ID) {
    return await fetchKHUser(payload.phoneNumber);
  }
  if (Number(payload.selectedBusinessVertical) === LEARN_VERTICAL_ID) {
    return await fetchLearnUser(payload.phoneNumber);
  }
};

export const useFetchCustomerInfo = () => {
  return useMutation({ mutationFn: fetchCustomerInfo });
};

const fetchCustomerOrderDetails = async (customerId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/fetch-customer-orders-details`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const payload = { uam_id: customerId };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchCustomerOrderDetails = (customerId) => {
  return useQuery({
    queryKey: ["customer_order_details", customerId],
    queryFn: () => fetchCustomerOrderDetails(customerId),
    enabled: !!customerId,
  });
};

const fetchOrderDetails = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-order-info/${orderId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, { headers });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchOrderDetails = (orderId) => {
  return useQuery({
    queryKey: ["order_details", orderId],
    queryFn: () => fetchOrderDetails(orderId),
    enabled: !!orderId,
  });
};

const markChequeReceived = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/update-cheque-status`
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useMarkChequeReceived = () => {
  return useMutation({ mutationFn: markChequeReceived });
};

const fetchChequeAcknowledgement = async (paymentId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-cheque-acknowledgement/${paymentId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to fetch cheque acknowledgement.`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching cheque acknowledgement."
    );
  }
};

export const useFetchChequeAcknowledgement = (payment_id) => {
  const query = useQuery({
    queryKey: ["ChequeAcknowledgement", payment_id],
    queryFn: () => fetchChequeAcknowledgement(payment_id),
    enabled: !!payment_id,
  });
  return {
    ...query,
    fetchChequeAcknowledgement,
  };
};

const banks = async () => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/get-banks-list`);
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};
export const useFetchAllBanks = () => {
  return useQuery({
    queryKey: ["Banks"],
    queryFn: () => banks(),
    staleTime: DAY,
    cacheTime: DAY,
    retry: QUERY_RETRY_ATTEMPTS,
  });
};

const fetchOrderDetailsByAdmissionNumber = async (applicationNumber) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-order-by-application-number/${applicationNumber}`
  );
  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data.order_id;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useFetchOrderDetailsByAdmissionNumber = () => {
  return useMutation({ mutationFn: fetchOrderDetailsByAdmissionNumber });
};

const fetchOrderInvoice = async (order_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-tax-invoice/${order_id}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch tax invoice.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching tax invoice."
    );
  }
};

export const useFetchOrderInvoice = (order_id) => {
  const query = useQuery({
    queryKey: ["OrderInvoice", order_id],
    queryFn: () => fetchOrderInvoice(order_id),
    enabled: !!order_id,
  });
  return {
    ...query,
    fetchOrderInvoice,
  };
};

const fetchOrderSummaryPDF = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/generate-or-get-order-summary/${orderId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchOrderSummaryPDF = (orderId) => {
  return useQuery({
    queryKey: ["OrderSummary", orderId],
    queryFn: () => fetchOrderSummaryPDF(orderId),
    enabled: false,
    retry: false,
  });
};

const fetchPaymentReceipt = async (payment_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-payment-receipt/${payment_id}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch Payment Receipt.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching Payment Receipt."
    );
  }
};

export const useFetchPaymentReceipt = (payment_id) => {
  const query = useQuery({
    queryKey: ["PaymentReceipt", payment_id],
    queryFn: () => fetchPaymentReceipt(payment_id),
    enabled: !!payment_id,
  });
  return {
    ...query,
    fetchPaymentReceipt,
  };
};

const fetchInvoiceDetails = async (paymentId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-payment-invoices-details/${paymentId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchInvoiceDetails = (paymentId) => {
  return useQuery({
    queryKey: ["InvoiceDetails", paymentId],
    queryFn: () => fetchInvoiceDetails(paymentId),
  });
};

const fetchPaymentTaxInvoice = async (invoiceNumber) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-heads-payment-invoice/${invoiceNumber}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch tax invoice. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the tax invoice."
    );
  }
};

export const useFetchPaymentTaxInvoice = (invoiceNumber) => {
  const query = useQuery({
    queryKey: ["paymentTaxInvoice", invoiceNumber],
    queryFn: () => fetchPaymentTaxInvoice(invoiceNumber),
    enabled: !!invoiceNumber,
  });
  return {
    ...query,
    fetchPaymentTaxInvoice,
  };
};
const fetchCenters = async () => {
  const gatewayTenantIdsString = localStorage.getItem("tenantIds");

  if (!gatewayTenantIdsString) return null;

  const gatewayTenantIds = gatewayTenantIdsString
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  const params = new URLSearchParams();

  gatewayTenantIds.forEach((id) => {
    params.append("gateway_tenant_id", id);
  });
  const url = new URL(
    `${
      process.env.REACT_APP_PACKAGE_MANAGEMENT_SERVICE_URL
    }/get-one-oms-tenants-from-gateway-tenants?${params.toString()}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SERVICE_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};
export const useFetchCenters = () => {
  return useQuery({
    queryKey: ["centers"],
    queryFn: () => fetchCenters(),
  });
};

const fetchBankDetails = async (organizationId, centerId) => {
  if (!organizationId || !centerId) {
    throw new Error("organizationId and centerId are required");
  }

  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/get-bank-details`);

  url.searchParams.append("billing_entity_id", organizationId);
  url.searchParams.append("tenant_id", centerId);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      accept: "application/json",
      "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data = await response.json();

  if (data?.status !== "success") {
    throw new Error(
      data?.message || "An error occurred while fetching bank details."
    );
  }

  if (!data?.data || (Array.isArray(data.data) && data.data.length === 0)) {
    throw new Error(
      "No bank details found for the provided organization and center."
    );
  }

  return data.data;
};

export const useFetchBankDetails = (organizationId, centerId) => {
  return useQuery({
    queryKey: ["bankDetails", organizationId, centerId],
    queryFn: () => fetchBankDetails(organizationId, centerId),
    enabled: Boolean(organizationId) && Boolean(centerId),
  });
};

const fetchCashiersCashBalance = async (centerId, organizationId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-cashiers-cash-balance?tenant_id=${centerId}&billing_entity_id=${organizationId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchCashiersCashBalance = (centerId, organizationId) => {
  return useQuery({
    queryKey: ["cashiersCashBalance", centerId, organizationId],
    queryFn: () => fetchCashiersCashBalance(centerId, organizationId),
    enabled: Boolean(centerId),
  });
};

const submitCashDeposit = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/submit-cash-deposit`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    try {
      const data = await response.json();
      throw new Error(
        data?.message ||
          "Oops! Something went wrong. Please refresh the page and try again."
      );
    } catch (error) {
      throw new Error(
        error?.message || "An error occurred while processing the request."
      );
    }
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data.id;
  }
  throw new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again."
  );
};
export const useSubmitCashDeposit = () => {
  return useMutation({ mutationFn: submitCashDeposit });
};

const fetchTransactionRecords = async (centerId, endTime) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-transaction-records`
  );

  url.search = new URLSearchParams({
    tenant_id: centerId,
    current_day_sheet_close_time: endTime,
  }).toString();

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
  };

  const response = await fetch(url.toString(), { headers });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchTransactionRecords = (filters, canFetch) => {
  const centerId = filters?.centerId;
  const endTime = toISTDateTime(filters?.endTime);

  return useQuery({
    queryKey: ["transactions", centerId, endTime],
    queryFn: () => fetchTransactionRecords(centerId, endTime),
    enabled: !!canFetch && !!centerId && !!endTime,
  });
};

const fetchRemainingCash = async (centerId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-remaining-cash`
  );

  url.search = new URLSearchParams({
    tenant_id: centerId,
  }).toString();

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
  };

  const response = await fetch(url.toString(), { headers });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.remaining_cash;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchRemainingCash = (filters, canFetch) => {
  const centerId = filters?.centerId;

  return useQuery({
    queryKey: ["remainingCash", centerId],
    queryFn: () => fetchRemainingCash(centerId),
    enabled: !!canFetch && !!centerId,
  });
};

const fetchLastDaySheet = async (centerId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-last-offline-day-sheet`
  );

  url.searchParams.append("tenant_id", centerId);

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    try {
      const data = await response.json();
      throw new Error(
        data?.message ||
          "Oops! Something went wrong. Please refresh the page and try again."
      );
    } catch (error) {
      throw new Error(
        error?.message || "An error occurred while processing the request."
      );
    }
  }

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching last day sheet."
    );
  }
};

export const useFetchLastDaySheet = (centerId) => {
  return useQuery({
    queryKey: ["lastDaySheet", centerId],
    queryFn: () => fetchLastDaySheet(centerId),
    enabled: !!centerId,
  });
};

const fetchCashDeposits = async (centerId, endTime) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-cash-deposit-details`
  );

  url.search = new URLSearchParams({
    tenant_id: centerId,
    current_day_sheet_close_time: endTime,
  }).toString();

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
  };

  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    try {
      const data = await response.json();
      throw new Error(
        data?.message ||
          "Oops! Something went wrong. Please refresh the page and try again."
      );
    } catch (error) {
      throw new Error(
        error?.message || "An error occurred while processing the request."
      );
    }
  }
  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching cash deposits."
    );
  }
};

export const useFetchCashDeposits = (filters, canFetch) => {
  const centerId = filters?.centerId;
  const endTime = toISTDateTime(filters?.endTime);

  return useQuery({
    queryKey: ["cashDeposits", centerId, endTime],
    queryFn: () => fetchCashDeposits(centerId, endTime),
    enabled: !!canFetch && !!centerId && !!endTime,
  });
};

const closeDaySheet = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/submit-offline-day-sheet`
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while closing day sheet."
    );
  }
};

export const useCloseDaySheet = () => {
  return useMutation({ mutationFn: closeDaySheet });
};

const downloadDaySheet = async (daySheetId, filters) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/download-day-sheet`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  url.searchParams.set("id", daySheetId);
  url.searchParams.set("type", "OFFLINE");
  url.searchParams.set("format", filters.format);

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file."
    );
  }
};

export const useDownloadDaySheet = () => {
  const query = useQuery({
    queryKey: ["downloadDaySheet"],
    queryFn: () => downloadDaySheet(),
  });
  return {
    ...query,
    downloadDaySheet: (daySheetId, filters) =>
      downloadDaySheet(daySheetId, filters),
  };
};

export const fetchDaySheets = async (filters) => {
  const { centerIds, startDate, endDate, applyFilter } = filters;

  if (!centerIds || !startDate || !endDate || !applyFilter) {
    throw new Error("Missing required filters to fetch day sheets");
  }

  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/get-day-sheets`);

  centerIds.forEach((centerId) => {
    url.searchParams.append("tenant_ids", centerId);
  });
  url.searchParams.set("day_sheet_close_time_from", startDate);
  url.searchParams.set("day_sheet_close_time_to", endDate);
  url.searchParams.set("type", "OFFLINE");

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
  };

  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || `Failed to fetch day sheets.`);
  }

  if (data?.status === "success") {
    return data.data;
  }

  throw new Error(
    data?.message || "An error occurred while fetching day sheets"
  );
};

export const useFetchDaySheets = ({ filters = {} }) => {
  const isEnabled = filters?.applyFilter === true;
  return useQuery({
    queryKey: ["daySheets", filters],
    queryFn: () => fetchDaySheets(filters),
    enabled: isEnabled,
  });
};
const updateCashStatus = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/update-cash-status`
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useUpdateCashStatus = () => {
  return useMutation({ mutationFn: updateCashStatus });
};
